import { useEffect, useState } from 'react';

export const useVideoOverlayVisibility = (
  videoElement: HTMLVideoElement | null,
  overlayRef: React.RefObject<HTMLElement>,
) => {
  const [overlayVisible, setOverlayVisible] = useState(true);

  function startOverlayHideTimer(delay = 2500) {
    return setTimeout(() => {
      setOverlayVisible(false);
    }, delay);
  }

  useEffect(() => {
    if (!videoElement) {
      return undefined;
    }
    let timeout: NodeJS.Timeout;

    const showOverlay = () => {
      setOverlayVisible(true);
      if (!videoElement.paused) {
        clearTimeout(timeout);
        timeout = startOverlayHideTimer();
      }
    };

    const hideOverlay = () => {
      if (!videoElement.paused) {
        clearTimeout(timeout);
        setOverlayVisible(false);
      }
    };

    const handlePlayPause = () => {
      if (videoElement.paused) {
        setOverlayVisible(true);
        clearTimeout(timeout);
      } else {
        showOverlay();
      }
    };

    const handleOverlayEnter = () => {
      showOverlay();
    };

    const handleOverlayLeave = () => {
      if (!videoElement.paused) {
        clearTimeout(timeout);
        timeout = startOverlayHideTimer();
      }
    };

    // Detect if mouse is already over video on page load
    requestAnimationFrame(() => {
      const isHovered =
        videoElement.matches(':hover') || videoElement.parentElement?.matches(':hover');
      if (isHovered && !videoElement.paused) {
        timeout = startOverlayHideTimer();
      }
    });

    videoElement.addEventListener('mousemove', showOverlay);
    videoElement.addEventListener('mouseenter', showOverlay);
    videoElement.addEventListener('mouseleave', hideOverlay);
    videoElement.addEventListener('touchstart', showOverlay);
    videoElement.addEventListener('pause', handlePlayPause);
    videoElement.addEventListener('play', handlePlayPause);
    overlayRef.current?.addEventListener('mouseenter', handleOverlayEnter);
    overlayRef.current?.addEventListener('mouseleave', handleOverlayLeave);

    return () => {
      videoElement.removeEventListener('mousemove', showOverlay);
      videoElement.removeEventListener('mouseenter', showOverlay);
      videoElement.removeEventListener('mouseleave', hideOverlay);
      videoElement.removeEventListener('touchstart', showOverlay);
      videoElement.removeEventListener('pause', handlePlayPause);
      videoElement.removeEventListener('play', handlePlayPause);
      overlayRef.current?.removeEventListener('mouseenter', handleOverlayEnter);
      overlayRef.current?.removeEventListener('mouseleave', handleOverlayLeave);
      clearTimeout(timeout);
    };
  }, [videoElement]);

  return overlayVisible;
};
